import { produce } from 'immer';
import { isIOS, isMobile } from 'react-device-detect';
import { AVATAR_TEXTURE_SCALE_FACTOR, AVATAR_TEXTURE_SIZE } from '../../constants/avatarConstants';
import { MAX_CARD_PIXEL_HEIGHT, MAX_NUM_SEATS } from '../../constants/constants';
import { getBaseAvatarScaleFactor } from '../../utils/avatarUtils';
import { isTestEnvironment } from '../../utils/utils';
import { CLEAR_GAME_DIMENSIONS_STATE, SET_IS_SELF_SITTING, SET_STAGE_DIMENSIONS, UPDATE_GAME_DIMENSIONS_FROM_GAME_SETTINGS } from '../actions/gameDimensionsActions';
const NAME_BADGE_H = isMobile ? 22 : 28;
const initialState = {
    numAvailableSeats: MAX_NUM_SEATS,
    allSeatBounds: [],
    dealerButtonCoords: []
};
function roundAllCoords(allCoords) {
    return allCoords.map((coords) => {
        return {
            x: Math.round(coords.x),
            y: Math.round(coords.y)
        };
    });
}
function createAllSeatBounds(seatDimensions) {
    const { w, h } = seatDimensions;
    const leftMostX = Math.round(w / 2);
    const topMostY = Math.round(h / 2);
    // seat index 0 is top center
    return [
        { x: leftMostX + 2 * w, y: topMostY, w, h }, // seat index 0
        { x: leftMostX + 3 * w, y: topMostY, w, h }, // seat index 1
        { x: leftMostX + 4 * w, y: topMostY, w, h }, // seat index 2
        { x: leftMostX + 4 * w, y: topMostY + h, w, h }, // seat index 3
        { x: leftMostX + 4 * w, y: topMostY + 2 * h, w, h }, // seat index 4
        { x: leftMostX + 3 * w, y: topMostY + 2 * h, w, h }, // seat index 5
        { x: leftMostX + w, y: topMostY + 2 * h, w, h }, // seat index 6
        { x: leftMostX, y: topMostY + 2 * h, w, h }, // seat index 7
        { x: leftMostX, y: topMostY + h, w, h }, // seat index 8
        { x: leftMostX, y: topMostY, w, h }, // seat index 9
        { x: leftMostX + w, y: topMostY, w, h } // seat index 10
    ];
}
/**
 * Moves specific seats from their default position (from an 11-seat table) to be spaced a bit better
 * for a given number of available seats.
 */
function createAdjustedAllSeatBounds(baseAllSeatBounds, seatDimensions, numAvailableSeats) {
    const { w, h } = seatDimensions;
    const quarterW = Math.floor(w / 4);
    const quarterH = Math.floor(h / 4);
    const thirdW = Math.floor(w / 3);
    const thirdH = Math.floor(h / 3);
    const halfH = Math.floor(h / 2);
    switch (numAvailableSeats) {
        case 4: {
            baseAllSeatBounds[5].x += quarterW;
            baseAllSeatBounds[6].x -= quarterW;
            return baseAllSeatBounds;
        }
        case 5: {
            baseAllSeatBounds[3].y -= halfH;
            baseAllSeatBounds[5].x += quarterW;
            baseAllSeatBounds[6].x -= quarterW;
            baseAllSeatBounds[8].y -= halfH;
            return baseAllSeatBounds;
        }
        case 6: {
            baseAllSeatBounds[1].x -= quarterW;
            baseAllSeatBounds[10].x += quarterW;
            return baseAllSeatBounds;
        }
        case 7: {
            baseAllSeatBounds[1].x += thirdW;
            baseAllSeatBounds[3].y += quarterH;
            baseAllSeatBounds[8].y += quarterH;
            baseAllSeatBounds[10].x -= thirdW;
            return baseAllSeatBounds;
        }
        case 8: {
            baseAllSeatBounds[1].x -= quarterW;
            baseAllSeatBounds[2].y += thirdH;
            baseAllSeatBounds[4].y -= thirdH;
            baseAllSeatBounds[7].y -= thirdH;
            baseAllSeatBounds[9].y += thirdH;
            baseAllSeatBounds[10].x += quarterW;
            return baseAllSeatBounds;
        }
        case 9: {
            baseAllSeatBounds[3].y -= halfH;
            baseAllSeatBounds[4].y -= halfH;
            baseAllSeatBounds[7].y -= halfH;
            baseAllSeatBounds[8].y -= halfH;
            return baseAllSeatBounds;
        }
        case 10: {
            baseAllSeatBounds[1].x -= thirdW;
            baseAllSeatBounds[2].x -= quarterW;
            baseAllSeatBounds[9].x += quarterW;
            baseAllSeatBounds[10].x += thirdW;
            return baseAllSeatBounds;
        }
        default: {
            return baseAllSeatBounds;
        }
    }
}
function createUsernameBarBounds(seatDimensions, roughAvatarSizePx) {
    const widthPaddingPx = 4;
    const { w, h } = seatDimensions;
    const contentBottomY = Math.min(roughAvatarSizePx / 2 + 35, h / 2);
    return {
        x: 0,
        y: Math.round(contentBottomY - NAME_BADGE_H / 2),
        w: w - 2 * widthPaddingPx,
        h: NAME_BADGE_H
    };
}
function createChipsOrStatusTopRight(usernameBarBounds) {
    return {
        x: Math.round(usernameBarBounds.w / 2),
        y: Math.round(usernameBarBounds.y - NAME_BADGE_H / 2 - (isMobile ? 20 : 24))
    };
}
function createBetPotCoords(allSeatBounds) {
    const { w, h } = allSeatBounds[0];
    return roundAllCoords([
        { x: allSeatBounds[0].x, y: allSeatBounds[0].y + h / 1.5 }, // top middle
        { x: allSeatBounds[1].x - w / 3, y: allSeatBounds[1].y + h / 1.5 },
        { x: allSeatBounds[2].x - w / 1.5, y: allSeatBounds[2].y + h / 1.5 },
        { x: allSeatBounds[3].x - w / 1.5, y: allSeatBounds[3].y }, // right middle
        { x: allSeatBounds[4].x - w / 1.5, y: allSeatBounds[4].y - h / 1.5 }, // bottom right
        { x: allSeatBounds[5].x - w / 3, y: allSeatBounds[5].y - h / 1.5 },
        { x: allSeatBounds[6].x + w / 3, y: allSeatBounds[6].y - h / 1.5 },
        { x: allSeatBounds[7].x + w / 1.5, y: allSeatBounds[7].y - h / 1.5 }, // bottom left
        { x: allSeatBounds[8].x + w / 1.5, y: allSeatBounds[8].y }, // left middle
        { x: allSeatBounds[9].x + w / 1.5, y: allSeatBounds[9].y + h / 1.5 },
        { x: allSeatBounds[10].x + w / 3, y: allSeatBounds[10].y + h / 1.5 }
    ]);
}
function createDealerButtonCoords(allSeatBounds) {
    const { w, h } = allSeatBounds[0];
    return roundAllCoords([
        { x: allSeatBounds[0].x - w / 4, y: allSeatBounds[0].y + h / 2 }, // top middle
        { x: allSeatBounds[1].x - w / 2, y: allSeatBounds[1].y + h / 2 },
        { x: allSeatBounds[2].x - w / 2, y: allSeatBounds[2].y + h / 2 },
        { x: allSeatBounds[3].x - w / 2, y: allSeatBounds[3].y }, // right middle
        { x: allSeatBounds[4].x - w / 2, y: allSeatBounds[4].y - h / 2 }, // bottom right
        { x: allSeatBounds[5].x - w / 2, y: allSeatBounds[5].y - h / 2 },
        { x: allSeatBounds[6].x + w / 2, y: allSeatBounds[6].y - h / 2 },
        { x: allSeatBounds[7].x + w / 2, y: allSeatBounds[7].y - h / 2 }, // bottom left
        { x: allSeatBounds[8].x + w / 2, y: allSeatBounds[8].y }, // left middle
        { x: allSeatBounds[9].x + w / 2, y: allSeatBounds[9].y + h / 2 },
        { x: allSeatBounds[10].x + w / 2, y: allSeatBounds[10].y + h / 2 }
    ]);
}
function createMainPotCoords(sideBetPhaseMessageCoords) {
    return {
        x: sideBetPhaseMessageCoords.x,
        y: sideBetPhaseMessageCoords.y + 55
    };
}
function createCommunityCardsBounds(tableAreaDimensions) {
    const totalCommunityCardsAreaW = Math.floor(tableAreaDimensions.w * (3 / 5));
    const totalCommunityCardsAreaH = Math.floor(tableAreaDimensions.h / 5);
    // reduce width of community cards area slightly to ensure a gap exists between community cards and shown player cards:
    const communityCardsAreaW = totalCommunityCardsAreaW * 0.9;
    const containerW = communityCardsAreaW;
    const containerH = Math.min(MAX_CARD_PIXEL_HEIGHT, totalCommunityCardsAreaH);
    return {
        x: Math.round(tableAreaDimensions.w / 2),
        y: Math.round(tableAreaDimensions.h / 2),
        w: containerW,
        h: containerH
    };
}
function createSideBetPhaseMessageCoords(communityCardsBounds) {
    return {
        x: communityCardsBounds.x,
        y: communityCardsBounds.y + Math.round(communityCardsBounds.h / 2) + 13
    };
}
function hasRoomForSecondBetButtonsRow(pokerBetControlsH) {
    if (isMobile) {
        // iPhones in particular have a home bar that easily obscures buttons at the very bottom (in PWA mode at least)
        // so don't attempt to render the tiny second row buttons in this case.
        // TODO: Revisit this after testing in various devices and finding a way to avoid the 'home bar'
        // or whatever other control might be at the very bottom.
        return false;
    }
    // in tests, allow for the auto-check button to show up with small-ish resolutions:
    const secondRowThresholdHeight = isTestEnvironment() ? 50 : 100;
    // if the height available for bet controls is tall enough, the second row button(s) will be rendered:
    return pokerBetControlsH > secondRowThresholdHeight;
}
function draftSetAll(draft) {
    if (!draft.stageDimensions) {
        console.error('Could not set game dimensions because stage dimensions are unknown');
        return;
    }
    delete draft.holeCardsAreaDimensions;
    delete draft.gameControlsDimensions;
    delete draft.foldButtonBounds;
    delete draft.checkButtonBounds;
    delete draft.callButtonBounds;
    delete draft.betButtonBounds;
    delete draft.raiseButtonBounds;
    delete draft.betSliderTopLeftBounds;
    delete draft.allInButtonBounds;
    delete draft.sitInButtonBounds;
    delete draft.sideBetButtonBounds;
    delete draft.setBetButtonGroupBounds;
    if (draft.isSelfSitting) {
        // sitting: reduce size of table area to make room for game controls
        const hasHomeBar = isMobile && isIOS;
        // On Mobile Safari (iOS) when Triplejack is running in "PWA" mode, it's not possible to hide the "home bar" at the bottom.
        // This provides some empty space under the bet buttons to make it easier to interact with them without activating the home bar:
        const homeBarPaddingPx = hasHomeBar ? 30 : 0;
        const minGameControlsHeightPx = hasHomeBar ? 90 : 72;
        const gameControlsHeightPx = Math.max(Math.ceil(draft.stageDimensions.h * 0.2), minGameControlsHeightPx);
        draft.gameControlsDimensions = {
            w: draft.stageDimensions.w,
            h: gameControlsHeightPx
        };
        draft.tableAreaDimensions = {
            w: draft.stageDimensions.w,
            h: draft.stageDimensions.h - draft.gameControlsDimensions.h
        };
        // if possible, make the hole cards area at least as wide as it is tall, and no less than 1/5 of the width and no more than 1/3 of the width:
        draft.holeCardsAreaDimensions = {
            w: Math.round(Math.min(Math.max(draft.tableAreaDimensions.w / 5, draft.gameControlsDimensions.h), draft.tableAreaDimensions.w / 3)),
            h: gameControlsHeightPx
        };
        // origin is top-left:
        const pokerBetControlsX = draft.holeCardsAreaDimensions.w;
        const pokerBetControlsY = draft.tableAreaDimensions.h;
        const pokerBetControlsW = draft.tableAreaDimensions.w - pokerBetControlsX;
        const pokerBetControlsH = draft.gameControlsDimensions.h;
        draft.mainBetControlsBounds = {
            x: pokerBetControlsX,
            y: pokerBetControlsY,
            w: pokerBetControlsW,
            h: pokerBetControlsH
        };
        if (draft.isAllInGame) {
            const betControlW = Math.floor(pokerBetControlsW / 2);
            const fullBetControlH = pokerBetControlsH - homeBarPaddingPx;
            const fullBetControlY = Math.round(pokerBetControlsY + fullBetControlH / 2);
            draft.foldButtonBounds = {
                x: pokerBetControlsX + Math.ceil(betControlW / 2),
                y: fullBetControlY,
                w: betControlW,
                h: fullBetControlH
            };
            draft.allInButtonBounds = {
                x: draft.foldButtonBounds.x + betControlW,
                y: fullBetControlY,
                w: betControlW,
                h: fullBetControlH
            };
        }
        else if (pokerBetControlsW / pokerBetControlsH < 2) {
            // render a 2x2 "grid" of bet buttons
            const betControlColumnH = pokerBetControlsH - homeBarPaddingPx; // the height of a column of bet buttons in 2x2 grid layout
            const betControlW = Math.floor(pokerBetControlsW / 2);
            const betControlH = Math.floor(betControlColumnH / 2);
            const topRowBetControlY = Math.round(pokerBetControlsY + betControlH / 2);
            draft.foldButtonBounds = {
                x: pokerBetControlsX + Math.ceil(betControlW / 2),
                y: topRowBetControlY,
                w: betControlW,
                h: betControlH
            };
            draft.checkButtonBounds = { ...draft.foldButtonBounds };
            draft.autoFoldAnyButtonBounds = { ...draft.foldButtonBounds };
            delete draft.autoCheckButtonBounds; // no room for auto check button
            draft.callButtonBounds = {
                ...draft.foldButtonBounds,
                y: draft.foldButtonBounds.y + betControlH
            };
            draft.betButtonBounds = {
                ...draft.foldButtonBounds,
                x: draft.foldButtonBounds.x + betControlW
            };
            draft.raiseButtonBounds = { ...draft.betButtonBounds };
            draft.betSliderTopLeftBounds = {
                x: pokerBetControlsX + betControlW,
                y: pokerBetControlsY + betControlH,
                w: betControlW,
                h: betControlH
            };
        }
        else {
            // render a single row for bet buttons with an optional second row for some auto-betting options
            const betControlW = Math.floor(pokerBetControlsW / 4);
            const fullBetControlH = pokerBetControlsH - homeBarPaddingPx;
            const fullBetControlY = Math.round(pokerBetControlsY + fullBetControlH / 2);
            if (hasRoomForSecondBetButtonsRow(pokerBetControlsH)) {
                const minAutoBetControlH = 24;
                const autoBetControlH = Math.max(minAutoBetControlH, Math.round(fullBetControlH * 0.3));
                const smallBetControlH = fullBetControlH - autoBetControlH;
                const smallBetControlY = Math.round(pokerBetControlsY + smallBetControlH / 2);
                const autoBetControlY = Math.round(pokerBetControlsY + smallBetControlH + autoBetControlH / 2);
                draft.foldButtonBounds = {
                    x: pokerBetControlsX + Math.ceil(betControlW / 2),
                    y: smallBetControlY,
                    w: betControlW,
                    h: smallBetControlH
                };
                draft.checkButtonBounds = { ...draft.foldButtonBounds };
                draft.autoFoldAnyButtonBounds = { ...draft.foldButtonBounds };
                draft.autoCheckButtonBounds = {
                    ...draft.foldButtonBounds,
                    y: autoBetControlY,
                    h: autoBetControlH
                };
                draft.callButtonBounds = {
                    ...draft.foldButtonBounds,
                    x: pokerBetControlsX + betControlW + Math.ceil(betControlW / 2)
                };
                draft.betButtonBounds = {
                    ...draft.foldButtonBounds,
                    x: pokerBetControlsX + 2 * betControlW + Math.ceil(betControlW / 2)
                };
                draft.raiseButtonBounds = { ...draft.betButtonBounds };
                draft.betSliderTopLeftBounds = {
                    x: pokerBetControlsX + 3 * betControlW,
                    y: pokerBetControlsY,
                    w: betControlW,
                    h: smallBetControlH
                };
                draft.setBetButtonGroupBounds = {
                    x: pokerBetControlsX + 3 * betControlW,
                    y: autoBetControlY,
                    w: betControlW * 2,
                    h: autoBetControlH
                };
            }
            else {
                draft.foldButtonBounds = {
                    x: pokerBetControlsX + Math.ceil(betControlW / 2),
                    y: fullBetControlY,
                    w: betControlW,
                    h: fullBetControlH
                };
                draft.checkButtonBounds = { ...draft.foldButtonBounds };
                draft.autoFoldAnyButtonBounds = { ...draft.foldButtonBounds };
                delete draft.autoCheckButtonBounds; // no room for auto check button
                draft.callButtonBounds = {
                    ...draft.foldButtonBounds,
                    x: pokerBetControlsX + betControlW + Math.ceil(betControlW / 2)
                };
                draft.betButtonBounds = {
                    ...draft.foldButtonBounds,
                    x: pokerBetControlsX + 2 * betControlW + Math.ceil(betControlW / 2)
                };
                draft.raiseButtonBounds = { ...draft.betButtonBounds };
                draft.betSliderTopLeftBounds = {
                    x: pokerBetControlsX + 3 * betControlW,
                    y: pokerBetControlsY,
                    w: betControlW,
                    h: fullBetControlH
                };
            }
        }
        draft.sitInButtonBounds = { ...draft.foldButtonBounds };
        draft.sideBetButtonBounds = { ...draft.foldButtonBounds };
    }
    else {
        // Not sitting (spectating): allow table area to take up full height since no controls are needed.
        // Add some padding at the bottom, for example to account for iOS 'home bar' at bottom center:
        draft.tableAreaDimensions = { w: draft.stageDimensions.w, h: Math.max(0, draft.stageDimensions.h - 15) };
    }
    const seatDimensions = {
        w: Math.floor(draft.tableAreaDimensions.w / 5),
        h: Math.floor(draft.tableAreaDimensions.h / 3)
    };
    draft.seatDimensions = seatDimensions;
    const baseAvatarScaleFactor = getBaseAvatarScaleFactor(AVATAR_TEXTURE_SIZE, AVATAR_TEXTURE_SIZE, seatDimensions.w, seatDimensions.h);
    draft.baseAvatarScaleFactor = baseAvatarScaleFactor;
    // bombs should be scaled independently from avatar texture sizes:
    draft.baseBombScaleFactor = baseAvatarScaleFactor * AVATAR_TEXTURE_SCALE_FACTOR;
    draft.roughAvatarSizePx = draft.baseAvatarScaleFactor * AVATAR_TEXTURE_SIZE * 0.8;
    const baseAllSeatBounds = createAllSeatBounds(seatDimensions);
    draft.usernameBarBounds = createUsernameBarBounds(seatDimensions, draft.roughAvatarSizePx);
    draft.chipsOrStatusTopRight = createChipsOrStatusTopRight(draft.usernameBarBounds);
    draft.betPotCoords = createBetPotCoords(baseAllSeatBounds);
    draft.allSeatBounds = createAdjustedAllSeatBounds(baseAllSeatBounds, seatDimensions, draft.numAvailableSeats);
    draft.dealerButtonCoords = createDealerButtonCoords(draft.allSeatBounds);
    draft.communityCardsBounds = createCommunityCardsBounds(draft.tableAreaDimensions);
    draft.sideBetPhaseMessageCoords = createSideBetPhaseMessageCoords(draft.communityCardsBounds);
    draft.mainPotCoords = createMainPotCoords(draft.sideBetPhaseMessageCoords);
}
const gameDimensionsReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case CLEAR_GAME_DIMENSIONS_STATE: {
                return initialState;
            }
            case SET_STAGE_DIMENSIONS: {
                draft.stageDimensions = action.payload;
                draftSetAll(draft);
                break;
            }
            case SET_IS_SELF_SITTING: {
                draft.isSelfSitting = action.payload;
                draftSetAll(draft);
                break;
            }
            case UPDATE_GAME_DIMENSIONS_FROM_GAME_SETTINGS: {
                const { numAvailableSeats, isAllInGame } = action.payload;
                draft.numAvailableSeats = numAvailableSeats;
                draft.isAllInGame = isAllInGame;
                draftSetAll(draft);
                break;
            }
        }
    });
};
export default gameDimensionsReducer;
